// Dati di Contracts V2: contratti, valori consentiti (Cluster, Type, Renewal Status), creazione, modifica,
// eliminazione ed export. Stesse API, regole e messaggi della pagina classica (pages/Contracts.js).
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { API_ENDPOINTS } from '../config/api.config';
import { ROLE_IDS } from '../config/roles.config';

/** Valore di contract_type che attiva il Current Monthly Fee (deve coincidere con il seed del backend). */
export const MONTHLY_RATE_VALUE = 'Monthly Rate';
export const NOTE_MAX_LENGTH = 60;

export const LOOKUP_FIELDS = [
  { key: 'cluster', field_key: 'contract_cluster', label: 'Cluster' },
  { key: 'type', field_key: 'contract_type', label: 'Type' },
  { key: 'renewal', field_key: 'contract_renewal_status', label: 'Renewal Status' },
];

async function request(url, options, fallback) {
  const res = await fetch(url, options);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) throw new Error(body?.error || fallback || `${res.status}: Request failed`);
  return body || {};
}

const jsonOptions = (method, payload) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });

/** Valori attivi di Cluster, Type e Renewal Status (vuoti se non disponibili, come nella pagina classica). */
export async function fetchActiveLookups() {
  const lists = await Promise.all(
    LOOKUP_FIELDS.map((f) =>
      fetch(`${API_ENDPOINTS.REF_LOOKUP}?field_key=${f.field_key}`)
        .then((res) => (res.ok ? res.json() : { items: [] }))
        .then((d) => d.items || [])
        .catch(() => []),
    ),
  );
  return Object.fromEntries(LOOKUP_FIELDS.map((f, i) => [f.key, lists[i]]));
}

/** Contratti con i dati di contorno: clienti, Service Manager, valori consentiti e progetti (per lo Yubiq code). */
export async function fetchContractsBundle() {
  const [c, customers, users, projects, lookups] = await Promise.all([
    request(API_ENDPOINTS.CONTRACTS, undefined, 'Error fetching contracts'),
    request(API_ENDPOINTS.CUSTOMERS).catch(() => ({ customers: [] })),
    request(API_ENDPOINTS.USERS).catch(() => ({ users: [] })),
    request(API_ENDPOINTS.PROJECTS).catch(() => ({ projects: [] })),
    fetchActiveLookups(),
  ]);
  const serviceManagers = (users.users || []).filter((u) => u.role_id === ROLE_IDS.SERVICE_MANAGER || u?.role?.id === ROLE_IDS.SERVICE_MANAGER);
  return { contracts: c.contracts || [], customers: customers.customers || [], serviceManagers, projects: projects.projects || [], lookups };
}

// ---- Permesso di gestire i valori consentiti ----
export const aclParams = ({ userEmail, userId, roleId }) => ({
  ...(userEmail ? { email: userEmail } : {}),
  ...(userId ? { user_id: userId } : {}),
  ...(roleId != null ? { role_id: roleId } : {}),
});

export async function fetchCanManageLookups(acl) {
  if (!acl.email && !acl.user_id) return false;
  const query = new URLSearchParams(Object.entries(acl).map(([k, v]) => [k, String(v)])).toString();
  try {
    const data = await request(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?action=can-manage&${query}`);
    return Boolean(data.can_manage);
  } catch (e) {
    return false;
  }
}

// ---- Form del contratto ----
export const EMPTY_CONTRACT_FORM = {
  customer_id: '',
  yubiq_code: '',
  cluster: '',
  tipologia_servizio: '',
  description: '',
  client_manager: '',
  service_manager: '',
  canone_mensile: '',
  importo_rinnovo: '',
  inizio_contratto: '',
  scadenza_contratto: '',
  stato_rinnovo: '',
  note: '',
};

export const contractToForm = (row) => ({
  customer_id: row.customer_id || '',
  yubiq_code: row.yubiq_code || '',
  cluster: row.cluster || '',
  tipologia_servizio: row.tipologia_servizio || '',
  description: row.description || '',
  client_manager: row.client_manager || '',
  service_manager: row.service_manager || '',
  canone_mensile: row.canone_mensile != null ? String(row.canone_mensile) : '',
  importo_rinnovo: row.importo_rinnovo != null ? String(row.importo_rinnovo) : '',
  inizio_contratto: row.inizio_contratto || '',
  scadenza_contratto: row.scadenza_contratto || '',
  stato_rinnovo: row.stato_rinnovo || '',
  note: row.note || '',
});

/** Cambiando Type, il Current Monthly Fee si azzera se il tipo non è più Monthly Rate (come nella pagina classica). */
export const changeContractForm = (form, field, value) => ({
  ...form,
  [field]: value,
  ...(field === 'tipologia_servizio' && value !== MONTHLY_RATE_VALUE ? { canone_mensile: '' } : {}),
});

// Campi obbligatori nello stesso ordine e con gli stessi nomi del messaggio della pagina classica
const REQUIRED = [
  ['customer_id', 'Customer', (f) => !f.customer_id],
  ['yubiq_code', 'Yubiq Code', (f) => !f.yubiq_code.trim()],
  ['cluster', 'Cluster', (f) => !f.cluster],
  ['tipologia_servizio', 'Type', (f) => !f.tipologia_servizio],
  ['client_manager', 'Client Manager', (f) => !f.client_manager.trim()],
  ['service_manager', 'Service Manager', (f) => !f.service_manager],
  ['canone_mensile', 'Current Monthly Fee', (f) => f.tipologia_servizio === MONTHLY_RATE_VALUE && f.canone_mensile === ''],
  ['importo_rinnovo', 'Renewal Monthly Fee', (f) => f.importo_rinnovo === ''],
  ['stato_rinnovo', 'Renewal Status', (f) => !f.stato_rinnovo],
  ['inizio_contratto', 'Contract Start Date', (f) => !f.inizio_contratto],
  ['scadenza_contratto', 'Contract End Date', (f) => !f.scadenza_contratto],
  ['description', 'Description', (f) => !f.description.trim()],
  ['note', 'Note', (f) => !f.note.trim()],
];

/** { message, fields }: message è quello della pagina classica, fields segna i campi da correggere. */
export function validateContractForm(form) {
  const missing = REQUIRED.filter(([, , isMissing]) => isMissing(form));
  if (missing.length) {
    return { message: `Required fields missing: ${missing.map(([, label]) => label).join(', ')}.`, fields: Object.fromEntries(missing.map(([key]) => [key, 'Required'])) };
  }
  if (form.scadenza_contratto < form.inizio_contratto) {
    return { message: 'Contract end date cannot be before contract start date.', fields: { scadenza_contratto: 'Must be on or after the start date' } };
  }
  return null;
}

const buildPayload = (form) => ({
  customer_id: parseInt(form.customer_id, 10),
  yubiq_code: form.yubiq_code.trim() || null,
  cluster: form.cluster || null,
  tipologia_servizio: form.tipologia_servizio || null,
  description: form.description.trim() || null,
  client_manager: form.client_manager.trim() || null,
  service_manager: form.service_manager || null,
  canone_mensile: form.tipologia_servizio === MONTHLY_RATE_VALUE && form.canone_mensile !== '' ? parseFloat(form.canone_mensile) : null,
  importo_rinnovo: form.importo_rinnovo !== '' ? parseFloat(form.importo_rinnovo) : null,
  inizio_contratto: form.inizio_contratto || null,
  scadenza_contratto: form.scadenza_contratto || null,
  stato_rinnovo: form.stato_rinnovo || null,
  note: form.note.trim() || null,
});

/** Crea (id null) o aggiorna il contratto; restituisce la risposta dell'API. */
export const saveContract = (id, form) =>
  request(id ? `${API_ENDPOINTS.CONTRACTS}?id=${id}` : API_ENDPOINTS.CONTRACTS, jsonOptions(id ? 'PUT' : 'POST', buildPayload(form)));

export const deleteContract = (id) => request(`${API_ENDPOINTS.CONTRACTS}?id=${id}`, { method: 'DELETE' }, 'Error deleting contract');

// ---- Valori consentiti (Manage Contract Values) ----
export async function fetchLookupValues(fieldKey) {
  const data = await request(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?field_key=${encodeURIComponent(fieldKey)}&include_inactive=true`, undefined, 'Unable to load lookup values');
  return data.items || [];
}

export function saveLookupValue({ editing, fieldKey, form, acl }) {
  if (!form.label.trim()) return Promise.reject(new Error('Label is required.'));
  const body = { ...acl, label: form.label.trim(), sort_order: Number(form.sort_order) || 0 };
  return editing
    ? request(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?id=${editing.id}`, jsonOptions('PUT', body), 'Save failed')
    : request(API_ENDPOINTS.CONTRACT_LOOKUPS, jsonOptions('POST', { ...body, field_key: fieldKey }), 'Save failed');
}

export const toggleLookupActive = (item, acl) =>
  request(`${API_ENDPOINTS.CONTRACT_LOOKUPS}?id=${item.id}`, jsonOptions('PUT', { ...acl, is_active: !item.is_active }), 'Update failed');

// ---- Visualizzazione ----
export function lookupLabel(options, value) {
  if (!value) return '';
  return (options || []).find((o) => o.value === value)?.label || value;
}

/** Le etichette bilingui ("Rinnovo concluso / Renewal finalized") si mostrano solo nella parte inglese. */
export const englishLabel = (label) => (label && label.includes(' / ') ? label.split(' / ').pop().trim() : label || '');

export const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Giorni alla scadenza (negativi se scaduto), null senza data di fine. */
export function daysToEnd(endDate, today = new Date()) {
  if (!endDate) return null;
  const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((end - start) / 86400000);
}

/** Export Excel: stesse colonne della pagina classica, delle righe passate. */
export function exportContracts(rows, lookups) {
  const data = rows.map((row) => ({
    Customer: row.customer_name || '',
    'Yubiq Code': row.yubiq_code || '',
    Cluster: lookupLabel(lookups.cluster, row.cluster) || row.cluster || '',
    Type: lookupLabel(lookups.type, row.tipologia_servizio) || row.tipologia_servizio || '',
    Description: row.description || '',
    'Client Manager': row.client_manager || '',
    'Service Manager': row.service_manager || '',
    'Current Monthly Fee': row.canone_mensile ?? '',
    'Renewal Monthly Fee': row.importo_rinnovo ?? '',
    'Renewal Status': lookupLabel(lookups.renewal, row.stato_rinnovo) || row.stato_rinnovo || '',
    'Contract Start Date': row.inizio_contratto || '',
    'Contract End Date': row.scadenza_contratto || '',
    Note: row.note || '',
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Contracts');
  const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' });
  saveAs(blob, `contracts_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
